import { PRODUCT_TYPES } from '@/types/sampleRequest.types';

// Session storage key for chat draft
export const CHAT_DRAFT_KEY = 'chatSampleRequestDraft';

export interface FaqCategory {
  id: number;
  name: string;
  icon?: string;
  itemCount: number;
  items?: FaqItem[];
}

export interface FaqItem {
  id: number;
  question: string;
  answer: string;
}

export interface ChatSource {
  chunkId: number;
  documentId: number;
  title: string;
  similarity: number;
  preview?: string;
  chunkIndex?: number;
}

export interface ChatAttachment {
  filename: string;
  mimeType: string;
  size: number;
  url: string;
}

export interface Message {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  tokens_used?: number;
  response_time_ms?: number;
  sources?: ChatSource[];
  attachments?: ChatAttachment[];
}

export interface ChatSession {
  id: number;
  session_id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface LocalMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  animate?: boolean;
}

// Chat Mode Type
export type ChatMode = 'initial' | 'question' | 'sample-request' | 'faq-browse';

// Enhanced Sample Request Form Steps with categories
export interface SampleRequestStep {
  field: string;
  label: string;
  placeholder: string;
  type: 'text' | 'select' | 'textarea' | 'number' | 'url' | 'image';
  options?: string[];
  required?: boolean;
  category: string;
  helpText?: string;
}

export const SAMPLE_REQUEST_STEPS: SampleRequestStep[] = [
  // Company Information (required for non-authenticated)
  { field: 'company_name', label: 'Company Name', placeholder: 'Your company name', type: 'text', required: true, category: 'Company Info', helpText: 'Enter your company or brand name' },
  { field: 'contact_person', label: 'Contact Person', placeholder: 'Your name', type: 'text', required: true, category: 'Company Info' },
  { field: 'email', label: 'Email Address', placeholder: 'your@email.com', type: 'text', required: true, category: 'Company Info', helpText: 'We will send tracking info to this email' },
  { field: 'phone', label: 'Phone Number', placeholder: '+1 234 567 8900', type: 'text', required: false, category: 'Company Info' },
  { field: 'country_code', label: 'Country', placeholder: 'Enter country code (e.g., US, KR)', type: 'text', required: false, category: 'Company Info', helpText: 'Two-letter country code (ISO 3166-1 alpha-2)' },

  // Product Basic Info
  { field: 'product_name', label: 'Product Name', placeholder: 'e.g., Vitamin C Brightening Serum', type: 'text', required: true, category: 'Product Info', helpText: 'What would you like to call this product?' },
  { field: 'product_type', label: 'Product Type', placeholder: 'Select type', type: 'select', required: true, category: 'Product Info', options: PRODUCT_TYPES },
  { field: 'volume', label: 'Volume/Size', placeholder: 'e.g., 30ml, 50g', type: 'text', required: false, category: 'Product Info' },

  // Market & Quantity
  { field: 'target_market_country', label: 'Target Market', placeholder: 'e.g., USA, EU', type: 'text', required: false, category: 'Market Info', helpText: 'Where will this product be sold?' },
  { field: 'estimated_launch_quantity', label: 'Launch Quantity', placeholder: 'e.g., 5000', type: 'number', required: false, category: 'Market Info', helpText: 'Estimated initial order quantity' },
  { field: 'target_cost_budget', label: 'Target Cost Range (USD)', placeholder: 'e.g., 3.00-5.00', type: 'text', required: false, category: 'Market Info' },

  // Formula & Ingredients
  { field: 'efficacy_function', label: 'Desired Efficacy', placeholder: 'e.g., Brightening, Anti-aging, Hydrating', type: 'text', required: false, category: 'Formula', helpText: 'What should this product do?' },
  { field: 'key_promotional_ingredients', label: 'Key Ingredients', placeholder: 'e.g., Vitamin C, Niacinamide, Hyaluronic Acid', type: 'text', required: false, category: 'Formula' },
  { field: 'restricted_ingredients', label: 'Ingredients to Avoid', placeholder: 'e.g., Parabens, Sulfates, Fragrance', type: 'text', required: false, category: 'Formula' },
  { field: 'formula_texture', label: 'Preferred Texture', placeholder: 'e.g., Lightweight gel, Rich cream', type: 'text', required: false, category: 'Formula' },

  // Packaging & Appearance
  { field: 'fragrance', label: 'Fragrance Preference', placeholder: 'e.g., Light floral, Fragrance-free', type: 'text', required: false, category: 'Packaging' },
  { field: 'color', label: 'Product Color', placeholder: 'e.g., Clear, White, Pink tint', type: 'text', required: false, category: 'Packaging' },
  { field: 'primary_packaging', label: 'Primary Packaging', placeholder: 'e.g., Glass bottle, Tube, Jar', type: 'text', required: false, category: 'Packaging' },
  { field: 'secondary_packaging', label: 'Secondary Packaging', placeholder: 'e.g., Box, Pouch', type: 'text', required: false, category: 'Packaging' },
  { field: 'package_reference_link', label: 'Package Reference Link', placeholder: 'https://...', type: 'url', required: false, category: 'Packaging', helpText: 'URL to packaging reference material or design' },
  { field: 'reference_packaging_image', label: 'Reference Image', placeholder: 'Upload an image', type: 'image', required: false, category: 'Packaging', helpText: 'Upload a reference image for packaging design' },

  // Reference & Benchmark
  { field: 'benchmark_product_name', label: 'Benchmark Product', placeholder: 'e.g., Brand X Vitamin C Serum', type: 'text', required: false, category: 'Reference', helpText: 'Any existing product you want us to reference?' },
  { field: 'benchmark_product_link', label: 'Reference Link', placeholder: 'https://...', type: 'url', required: false, category: 'Reference', helpText: 'Link to the benchmark product or inspiration' },
  { field: 'preferred_attributes', label: 'What You Like', placeholder: 'Features you want to include', type: 'textarea', required: false, category: 'Reference' },
  { field: 'non_preferred_attributes', label: 'What to Avoid', placeholder: 'Features you want to avoid', type: 'textarea', required: false, category: 'Reference' },

  // Additional
  { field: 'marketing_brand_claims', label: 'Brand Claims', placeholder: 'e.g., Vegan, Cruelty-free, Organic', type: 'text', required: false, category: 'Additional' },
  { field: 'additional_requests_comments', label: 'Additional Comments', placeholder: 'Any other requirements or notes...', type: 'textarea', required: false, category: 'Additional' },
];
